import type React from "react";
import { useState } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { TutorialCategory } from "../types/tutorial";
import { colors } from "../constants/colors";

const mockCategories: TutorialCategory[] = [
  {
    id: "general-flow",
    title: "General Flow",
    description: "Learn the basic workflow of the application",
    slides: [
      {
        id: "step1",
        title: "Step 1",
        content: "Welcome to the tutorial! This is step 1 of the general flow.",
      },
      {
        id: "step2",
        title: "Step 2",
        content: "This is step 2 of the general flow.",
      },
    ],
  },
  {
    id: "truck-management",
    title: "Truck Management",
    description: "Learn how to manage trucks",
    slides: [
      {
        id: "truck-step1",
        title: "Step 1",
        content: "Welcome to truck management! This is step 1.",
      },
      {
        id: "truck-step2",
        title: "Step 2",
        content: "This is step 2 of truck management.",
      },
    ],
  },
];

const TutorialPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [selectedCategory, setSelectedCategory] = useState<TutorialCategory | null>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const handleCategorySelect = (category: TutorialCategory) => {
    setSelectedCategory(category);
    setCurrentSlideIndex(0);
  };

  const handleNextSlide = () => {
    if (selectedCategory && currentSlideIndex < selectedCategory.slides.length - 1) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    }
  };

  const handlePreviousSlide = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={3}
        sx={{
          display: "flex",
          minHeight: "calc(100vh - 120px)",
          overflow: "hidden",
          borderRadius: "12px",
          boxShadow: colors.shadow.light.medium,
        }}
      >
        {/* Left side - Categories */}
        <Box
          sx={{
            width: "30%",
            borderRight: 1,
            borderColor: "divider",
            overflow: "auto",
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <List>
            {mockCategories.map((category) => (
              <ListItem key={category.id} disablePadding>
                <ListItemButton
                  selected={selectedCategory?.id === category.id}
                  onClick={() => handleCategorySelect(category)}
                  sx={{
                    "&.Mui-selected": {
                      backgroundColor: colors.componentHighlight,
                      "&:hover": {
                        backgroundColor: colors.componentHighlight,
                      },
                    },
                    "&:hover": {
                      backgroundColor: colors.componentHighlight,
                    },
                    "& .MuiTouchRipple-root": {
                      color: colors.rippleEffect,
                    },
                    py: 2,
                  }}
                >
                  <ListItemText
                    primary={category.title}
                    secondary={category.description}
                    primaryTypographyProps={{
                      variant: "subtitle1",
                      fontWeight: selectedCategory?.id === category.id ? 600 : 400,
                      color: selectedCategory?.id === category.id ? "primary.main" : "text.primary",
                    }}
                    secondaryTypographyProps={{
                      variant: "body2",
                      color: "text.secondary",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Right side - Slides */}
        <Box
          sx={{
            flex: 1,
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
            backgroundColor: theme.palette.background.default,
          }}
        >
          {selectedCategory ? (
            <>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  textAlign: "center",
                }}
              >
                {selectedCategory.slides[currentSlideIndex].title}
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.7,
                  fontSize: "0.95rem",
                  maxWidth: "800px",
                  textAlign: "center",
                }}
              >
                {selectedCategory.slides[currentSlideIndex].content}
              </Typography>
              {selectedCategory.slides[currentSlideIndex].imageUrl && (
                <Box
                  component="img"
                  src={selectedCategory.slides[currentSlideIndex].imageUrl}
                  alt={selectedCategory.slides[currentSlideIndex].title}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: 300,
                    mt: 2,
                    borderRadius: "8px",
                    boxShadow: colors.shadow.light.small,
                  }}
                />
              )}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 16,
                  left: 0,
                  right: 0,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                }}
              >
                <IconButton
                  onClick={handlePreviousSlide}
                  disabled={currentSlideIndex === 0}
                  sx={{
                    "&:hover": {
                      backgroundColor: colors.componentHighlight,
                    },
                    "&.Mui-disabled": {
                      color: theme.palette.action.disabled,
                    },
                  }}
                >
                  <ChevronLeft />
                </IconButton>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontWeight: 500,
                  }}
                >
                  {currentSlideIndex + 1} / {selectedCategory.slides.length}
                </Typography>
                <IconButton
                  onClick={handleNextSlide}
                  disabled={currentSlideIndex === selectedCategory.slides.length - 1}
                  sx={{
                    "&:hover": {
                      backgroundColor: colors.componentHighlight,
                    },
                    "&.Mui-disabled": {
                      color: theme.palette.action.disabled,
                    },
                  }}
                >
                  <ChevronRight />
                </IconButton>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: theme.palette.text.secondary,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                }}
              >
                Select a tutorial category to begin
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  textAlign: "center",
                }}
              >
                Choose from the list on the left to start learning
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default TutorialPage;
